import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Edit,
  InsertDriveFile,
  PictureAsPdf,
  Clear,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyLoading } from "../../../shared-components/Loading";
import Tooltip from "@mui/material/Tooltip";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";

const ALLOWED_FILE_TYPES = [
  "csv",
  "json",
  "docx",
  "xls",
  "xlsx",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/json",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const BulkClassUploadDialog = ({
  open,
  setOpen,
  schoolClass,
  onReload = () => {},
}) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [suggestedStudents, setSuggestedStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const ApiClient = useApiClient();
  const handleClose = () => {
    setSuggestedStudents([]);
    setLoading(false);
    setOpen(false);
    setFile(null);
  };
  const handleExtract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const response = await ApiClient.schools.extractStudentsFromFile({
        file: file,
        schoolClass: schoolClass.id,
      });
      if (response.success) {
        setSuggestedStudents(response.suggestedStudents.Students);
        enqueueSnackbar("Students extracted successfully!", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Failed to extract students.", { variant: "error" });
      }
    } catch (error) {
      console.error("Upload failed", error);
      enqueueSnackbar("Something went wrong during extraction.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    try {
      const response = await ApiClient.schools.bulkUploadStudents({
        suggestedStudentListJson: JSON.stringify(
          suggestedStudents.filter((s) => !s.DetailsDiffer),
        ),
        schoolClass: schoolClass.id,
      });
      if (response.success) {
        handleClose();
        onReload();
        enqueueSnackbar("Students successfully added!", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Failed to add students.", { variant: "error" });
      }
    } catch (error) {
      console.error("Upload failed", error);
      enqueueSnackbar("Something went wrong during upload.", {
        variant: "error",
      });
    }
  };

  const handleStudentChange = (index, field, value) => {
    setSuggestedStudents((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleRemoveStudent = (index) => {
    setSuggestedStudents((prev) => prev.filter((_, i) => i !== index));
  };

  const renderStudentRow = (student, index) => (
    <Stack
      key={index}
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ mb: 1 }}
    >
      <Tooltip
        title={
          student.IsNew
            ? "Student will be added to the system"
            : student.DetailsDiffer
              ? "Details do not match existing student"
              : "Student exists and details match"
        }
      >
        <Chip
          label={
            student.IsNew ? "New" : student.DetailsDiffer ? "Error" : "Match"
          }
          color={
            student.IsNew ? "success" : student.DetailsDiffer ? "error" : "info"
          }
          size="small"
        />
      </Tooltip>
      <TextField
        disabled={!student.IsNew}
        label="Identifier"
        value={student.StudentIdentifier || ""}
        onChange={(e) =>
          handleStudentChange(index, "StudentIdentifier", e.target.value)
        }
        size="small"
        sx={{ width: 150 }}
      />
      <TextField
        disabled={!student.IsNew}
        label="First Name"
        value={student.FirstName || ""}
        onChange={(e) =>
          handleStudentChange(index, "FirstName", e.target.value)
        }
        size="small"
        sx={{ width: 150 }}
      />
      <TextField
        disabled={!student.IsNew}
        label="Last Name"
        value={student.LastName || ""}
        onChange={(e) => handleStudentChange(index, "LastName", e.target.value)}
        size="small"
        sx={{ width: 150 }}
      />
      <TextField
        disabled={!student.IsNew}
        label="Email"
        value={student.Email || ""}
        onChange={(e) => handleStudentChange(index, "Email", e.target.value)}
        size="small"
        sx={{ width: 150 }}
      />
      <IconButton
        aria-label="Remove student"
        onClick={() => handleRemoveStudent(index)}
        size="small"
        sx={{ ml: 1, border: 0 }}
      >
        <Clear fontSize="small" />
      </IconButton>
    </Stack>
  );

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    const ext = uploadedFile.name.split(".").pop().toLowerCase();
    if (
      ALLOWED_FILE_TYPES.includes(ext) ||
      ALLOWED_FILE_TYPES.includes(uploadedFile.type)
    ) {
      setFile(uploadedFile);
    } else {
      enqueueSnackbar(
        "Invalid file type. Please upload a CSV, JSON, Word, or Excel file.",
        {
          variant: "error",
          autoHideDuration: 3000,
        },
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{ paper: { sx: { backgroundImage: "none" } } }}
    >
      <DialogTitle>Upload Students to Class</DialogTitle>
      <DialogContent>
        {!loading && suggestedStudents.length === 0 && (
          <FormControl fullWidth>
            {/* Instruction Text */}
            <Typography variant="subtitle1" gutterBottom>
              Upload a student list for <strong>{schoolClass?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Accepted file types: <strong>.csv</strong>, <strong>.json</strong>
              , <strong>.docx</strong>, <strong>.xls</strong>,{" "}
              <strong>.xlsx</strong>
            </Typography>

            {/* Clickable drag-and-drop area */}
            <Box
              component="label"
              htmlFor="student-upload"
              sx={{
                mt: 2,
                border: "2px dashed",
                borderColor: dragging ? "primary.main" : "divider",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: dragging ? "action.selected" : "transparent",
                transition: "border 0.2s, background-color 0.2s",
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFileUpload({ target: { files: e.dataTransfer.files } });
              }}
            >
              <input
                id="student-upload"
                data-testid="student-upload-input"
                type="file"
                hidden
                accept=".csv,.json,.docx,.xls,.xlsx"
                onChange={handleFileUpload}
              />

              <Stack alignItems="center" spacing={1}>
                <Box sx={{ display: "flex", gap: 1, color: "text.secondary" }}>
                  <Edit />
                  <InsertDriveFile />
                  <PictureAsPdf />
                </Box>
                <Typography variant="body1">
                  {file
                    ? `Selected: ${file.name}`
                    : "Click or drag a file to upload"}
                </Typography>
              </Stack>
            </Box>
          </FormControl>
        )}

        {loading && <FruitifyLoading category={MESSAGE_CATEGORIES.aiMagic} />}
        {!loading && suggestedStudents.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {/* Students to be added */}
            <Typography variant="h6" gutterBottom>
              New students
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              These are brand new students who will be added to the class
            </Typography>
            {suggestedStudents
              .filter((s) => s.DetailsDiffer)
              .map(renderStudentRow)}

            {/* Students not being added due to existing record */}
            <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
              Matched students
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              These students already exist and will be added to the class
            </Typography>
            {suggestedStudents
              .filter((s) => !s.DetailsDiffer)
              .map(renderStudentRow)}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={suggestedStudents.length == 0 ? handleExtract : handleSubmit}
          variant="contained"
          disabled={!schoolClass || !file}
        >
          {suggestedStudents.length == 0 ? "Extract" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
